import { useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import {
  type CreateManualDeadlineRequest,
  type FilingDeadlineDto,
  type RecognizedReportTypeDto,
  manualDeadlineErrorCopy,
  manualDeadlineWarningCopy,
} from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  useCreateManualDeadline,
  useRecognizedReportTypes,
  CreateManualDeadlineError,
} from '@/hooks/queries/useFinanceDeadlines';
import { useFinanceSettings } from '@/hooks/queries/useFinanceSettings';
import { ReportTypeCombobox } from './ReportTypeCombobox';
import { readySettings } from '@/lib/financeSettingsRead';

/**
 * #132 / #121 — the treasurer-facing form to add a reporting period by hand. For a
 * jurisdiction whose filing calendar the app can't fully auto-generate (Virginia's
 * election-relative periods), the treasurer enters the coverage window + due date + an
 * optional report code. Wires `POST /api/finance/deadlines/manual` and surfaces the
 * server's contiguity / non-overlap / range verdict as treasurer copy
 * (`manualDeadlineErrorCopy`) — never a raw wire code. Jurisdiction-neutral: any
 * Finance.Manage treasurer can add a period; there is no `stateCode === 'VA'` gate.
 */
export function NewReportingPeriodForm({
  onCreated,
  onCancel,
  reportTypeOptions,
}: {
  onCreated?: (deadline: FilingDeadlineDto) => void;
  onCancel?: () => void;
  /**
   * The jurisdiction's recognized report types (#220). When present, the report-code field
   * is a discoverable picker (code + plain-English name) that still allows a free-text code;
   * when absent/empty (a jurisdiction with no code list, e.g. VA) it stays a plain text box.
   */
  reportTypeOptions?: RecognizedReportTypeDto[];
}) {
  const { createManualDeadline } = useCreateManualDeadline();
  const [name, setName] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [reportCode, setReportCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A non-blocking advisory (#220): the period was created, but the entered report code
  // isn't one we recognize for this jurisdiction. We keep the dialog open showing this so
  // the treasurer sees it (rather than auto-closing) — warn, don't block.
  const [warning, setWarning] = useState<string | null>(null);

  // The server owns the real validation (contiguity, non-overlap, range); this only
  // guards the obviously-incomplete submit so the first click isn't a guaranteed 400.
  const canSubmit =
    name.trim() !== '' && periodStart !== '' && periodEnd !== '' && dueDate !== '' && !busy;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    const request: CreateManualDeadlineRequest = {
      name: name.trim(),
      periodStart,
      periodEnd,
      dueDate,
      reportCode: reportCode.trim() || null,
    };
    try {
      const { deadline, reportCodeWarning } = await createManualDeadline(request);
      // The period was created either way. On an unrecognized-code advisory, hold the dialog
      // open and surface it (the list already refreshed); otherwise close as usual.
      const warningCopy = reportCodeWarning
        ? manualDeadlineWarningCopy(reportCodeWarning, reportCode.trim())
        : null;
      if (warningCopy) {
        setWarning(warningCopy);
      } else {
        onCreated?.(deadline);
      }
    } catch (err) {
      // A validation refusal carries the wire code → render its treasurer copy. Anything
      // else (network, unknown) falls back to the generic (never-blank) message.
      setError(
        err instanceof CreateManualDeadlineError
          ? manualDeadlineErrorCopy(err.code)
          : manualDeadlineErrorCopy(undefined),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="new-reporting-period-form" className="space-y-4">
      <div className="space-y-1">
        <Label htmlFor="nrp-name">Name</Label>
        <Input
          id="nrp-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Second Semi-Annual 2026"
          data-testid="new-reporting-period-name"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="nrp-start">Coverage start</Label>
          <Input
            id="nrp-start"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.target.value)}
            data-testid="new-reporting-period-start"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="nrp-end">Coverage end</Label>
          <Input
            id="nrp-end"
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.target.value)}
            data-testid="new-reporting-period-end"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="nrp-due">Filing due date</Label>
          <Input
            id="nrp-due"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            data-testid="new-reporting-period-due"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="nrp-code">Report code (optional)</Label>
          {reportTypeOptions && reportTypeOptions.length > 0 ? (
            <ReportTypeCombobox
              id="nrp-code"
              value={reportCode}
              onChange={setReportCode}
              options={reportTypeOptions}
              testId="new-reporting-period-code"
            />
          ) : (
            <Input
              id="nrp-code"
              value={reportCode}
              onChange={(e) => setReportCode(e.target.value)}
              placeholder="e.g. SA"
              data-testid="new-reporting-period-code"
            />
          )}
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive" data-testid="new-reporting-period-error">
          {error}
        </p>
      )}

      {warning && (
        <p
          role="status"
          className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="new-reporting-period-warning"
        >
          {warning}
        </p>
      )}

      <div className="flex justify-end gap-2">
        {warning ? (
          // The period is already created — the only action left is to acknowledge + close.
          <Button
            type="button"
            onClick={() => onCancel?.()}
            data-testid="new-reporting-period-done"
          >
            Done
          </Button>
        ) : (
          <>
            {onCancel && (
              <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
                Cancel
              </Button>
            )}
            <Button type="submit" disabled={!canSubmit} data-testid="new-reporting-period-submit">
              {busy ? 'Creating…' : 'Create reporting period'}
            </Button>
          </>
        )}
      </div>
    </form>
  );
}

/**
 * The "New reporting period" affordance for the reports list — a Finance.Manage treasurer
 * opens this dialog to hand-add a filing period (mounts {@link NewReportingPeriodForm}).
 * The parent gates on the capability; the trigger closes the dialog on a successful create.
 */
export function NewReportingPeriodDialog() {
  const [open, setOpen] = useState(false);
  // The report-code picker is driven by the campaign's own jurisdiction: resolve its ruleset
  // from finance settings, then load that jurisdiction's recognized report types. A no-list
  // jurisdiction (VA) yields an empty list → the form falls back to a plain text box.
  const settings = readySettings(useFinanceSettings());
  const { data: reportTypeOptions } = useRecognizedReportTypes(settings?.rulesetCode);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="new-reporting-period-trigger">
          <Plus className="mr-1 h-4 w-4" />
          New reporting period
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New reporting period</DialogTitle>
          <DialogDescription>
            Add a filing period your calendar doesn&apos;t generate automatically. Enter the
            coverage window and the filing due date; we&apos;ll check it doesn&apos;t overlap or
            leave a gap after the previous period.
          </DialogDescription>
        </DialogHeader>
        <NewReportingPeriodForm
          reportTypeOptions={reportTypeOptions}
          onCreated={() => setOpen(false)}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
